import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Button, Divider, dividerClasses, Grid, Icon, IconButton, Tooltip } from '@mui/material';
import { Box, styled } from '@mui/system';
import update from 'immutability-helper';
import queryString from 'query-string';

import Breadcrumbs from 'Lib/components/common/Breadcrumbs';
import Files from 'Lib/components/common/table/controls/Files';
import { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import useApi from 'Lib/hooks/useApi';
import getIn from 'Lib/utilities/getIn';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

import FiltersList from '../page-layout/FiltersList';

import { Options, Search, Settings, TableView } from './controls/index';
import SavedSearch from './controls/SavedSearch';
import { VIEWS } from './controls/TableView';
import ChipMap from './filters/chips/ChipMap';
import { ChipContainer } from './filters/chips/index';
import { CardTable, ListTable } from './index';

/**
 * Table component with flexible configuration and features.
 *
 * Props:
 * - `columns`: Array of column configuration objects.
 * - `rows`: Array of row objects with render functions.
 * - `renderDetails`: Function for rendering additional details.
 * - `tableFooter`: Object for configuring the table footer.
 * - `tableExpanded`: Function for rendering expandable rows.
 * - `actions`: Array of action objects for row actions.
 * - `loading`: Boolean to control the loading state.
 * - `checkable`: String representing the data path for row selection.
 * - `editable`: Boolean indicating if rows are editable.
 * - `deletable`: Boolean indicating if rows are deletable.
 * - `fetch`: Function to fetch list data.
 * - `data`: Array of data objects for the table.
 * - `to`: Function for navigation control on row click.
 * - `emptyMessage`: String for the message displayed when no data is present.
 * - `emptyIcon`: String for the icon displayed when no data is present.
 * - `search`: String placeholder for the search input.
 * - `striped`: Boolean for striped row style.
 * - `onSelected`: Function called when rows are selected.
 * - `selectedActions`: Object with actions for selected rows.
 * - `setClearSelected`: Function to clear selected rows.
 */

export default function Table(props) {
	const history = useHistory();
	const dispatch = useDispatch();
	const { pageSize } = useSelector(state => state.ui.table);
	const listViewID = useSelector(state => state.ui.table.listViewID);

	const { toggleDrawer, setTableInformation, setSavedSearches, setTableView } = props.common.actions;
	const { getSavedLists } = props.common.api || {};

	const fetchSearches = useApi(getSavedLists);

	const { mobile, smallMobile } = screenSize();

	const [selected, setSelected] = useState([]);
	const [showMore, setShowMore] = useState(false);

	const showTableControls = !props.loading && mobile && !smallMobile;
	const showTableControlsDivider = props.search || props.filters || props.listTypeID || props.files;

	// Omit page, pageSize, orderBy, & search from filters (do not remove these)
	// eslint-disable-next-line no-unused-vars
	const { page, search, orderBy, tab, ...activeFilters } = queryString.parse(history.location.search);
	const areFiltersPresent = !isEmpty(activeFilters) && !(Object.keys(activeFilters).length === 1 && ['tab', 'pageSize'].includes(Object.keys(activeFilters)[0]));

	const areAllSelected = props.data.length > 0 && props.data.every(item => selected.find(s => getIn(s, props.checkable(s)) == getIn(item, props.checkable(item))));
	const areSomeSelected = props.data.length > 0 && props.data.some(item => selected.find(s => getIn(s, props.checkable(s)) == getIn(item, props.checkable(item))));

	const fileUploadRef = useRef();
	const { getRootProps, getInputProps } = useDropzone({
		noClick: true,
		onFocus: null,
		onBlur: null,
		onDrop: acceptedFiles => {
			props.onDropFiles(acceptedFiles);
			fileUploadRef.current.value = null; // Clear the file input to allow uploading same files again
		}
	});

	useEffect(() => {
		dispatch(setTableInformation({ listTypeID: props.listTypeID }));

		if (smallMobile || props.defaultCardView) dispatch(setTableView(VIEWS.CARD));

		// Fetch saved searches related to the currently viewed table
		if (props.listTypeID && props.search && !props.files) {
			fetchSearches({ listReportTypeID: props.listTypeID, pageSize: 10000 }).then(response => {
				dispatch(setSavedSearches(response.data));
			});
		}

		if (props.selectedActions) props.setClearSelected(() => () => setSelected([]));

		// Triggers fetch files since FiltersList won't trigger initial fetch
		if (props.files) handleFilters();
	}, []);

	useEffect(() => {
		if (props.onSelected) props.onSelected(selected);
	}, [selected]);

	const handleCheckboxSelect = (event, item) => {
		event.stopPropagation();

		if (event.target.checked) {
			setSelected(prevState => update(prevState, { $push: [item] }));
		} else {
			const index = selected.map(s => getIn(s, props.checkable(s))).indexOf(getIn(item, props.checkable(item)));

			setSelected(prevState => update(prevState, { $splice: [[index, 1]] }));
		}
	};

	const handleCheckboxSelectAll = event => {
		if (event.target.checked) {
			const newSelections = props.data.filter(
				item => !selected.find(selectedItem => getIn(selectedItem, props.checkable(selectedItem)) === getIn(item, props.checkable(item)))
			);
			setSelected(prevState => [...prevState, ...newSelections]);
		} else {
			setSelected(
				selected.reduce((acc, curr) => {
					const found = props.data.find(d => getIn(d, props.checkable(d)) == getIn(curr, props.checkable(curr)));

					if (!found) acc.push(curr);

					return acc;
				}, [])
			);
		}
	};

	const handleFilters = (filters = {}) => {
		const previousQueryString = queryString.parse(history.location.search);
		const updatedFilterQueryString = { ...previousQueryString, ...filters, pageSize };
		if (props.fetch) {
			props.fetch(updatedFilterQueryString);

			return;
		}
	};

	const handleDelete = filterKeys => {
		const updatedUrl = queryString.exclude(history.location.search, Array.isArray(filterKeys) ? filterKeys : [filterKeys]);

		history.push(history.location.pathname + updatedUrl);
		if (props.fetch) {
			props.fetch(queryString.parse(updatedUrl));
		}
	};

	const renderChips = () => {
		const clonedActiveFilters = {};

		/* Checks if filter Min and/or Max pairs are found and combines them into a single filter */
		Object.keys(activeFilters).forEach(filterName => {
			if (['Min', 'Max'].includes(filterName.slice(-3))) {
				const filter = filterName.replace(filterName.slice(-3), '');
				const hasMin = `${filter}Min` in activeFilters;
				const hasMax = `${filter}Max` in activeFilters;

				if (hasMin && !hasMax) clonedActiveFilters[filter] = `Starting from ${activeFilters[`${filter}Min`]}`;
				else if (!hasMin && hasMax) clonedActiveFilters[filter] = `Up to ${activeFilters[`${filter}Max`]}`;
				else clonedActiveFilters[filter] = `${activeFilters[`${filter}Min`]} to ${activeFilters[`${filter}Max`]}`;
			} else clonedActiveFilters[filterName] = activeFilters[filterName];
		});

		const viewableFilters = Object.entries(clonedActiveFilters).slice(0, 4);
		const hiddenFilters = Object.entries(clonedActiveFilters).slice(4);

		const buildChip = (key, value, index) => {
			if (isEmpty(props.filters)) return null;

			const filter = props.filters.buttons.find(filter => filter.name == key);
			if (!filter) return null;

			const Component = filter.enumeration ? ChipMap['enumeration'] : ChipMap[filter.type];

			return (
				<Component
					{...props.common}
					key={`${key}_${index}`}
					filter={filter}
					value={value}
					handleDelete={handleDelete}
					onClick={() => dispatch(toggleDrawer(DRAWER_CONTENT_TYPES.FILTERS))}
				/>
			);
		};

		const renderViewable = () => viewableFilters?.map(([key, value], index) => buildChip(key, value, index));

		const renderHidden = () => hiddenFilters?.map(([key, value], index) => buildChip(key, value, index));

		return (
			<Grid container>
				<Grid item xs={10}>
					{renderViewable()}
					{showMore ? renderHidden() : null}
				</Grid>
				<Grid item xs={2}>
					{hiddenFilters.length > 0 ? (
						<Button endIcon={<Icon>expand_more</Icon>} onClick={() => setShowMore(prevState => !prevState)} sx={{ float: 'right', mt: 2 }}>
							{showMore ? 'Hide' : `+ ${hiddenFilters.length} More`}
						</Button>
					) : null}
				</Grid>
			</Grid>
		);
	};

	const renderTableView = () => {
		const extendedProps = {
			selected,
			areAllSelected,
			areSomeSelected,
			handleCheckboxSelect,
			handleCheckboxSelectAll,
			...props
		};

		const TableViewComponent = listViewID === VIEWS.LIST || isEmpty(props.views) || !props.renderDetails ? ListTable : CardTable;

		return (
			<Box {...(props.files && !props.disableUpload ? getRootProps() : {})} height="100%" display="contents" sx={{ cursor: 'inherit' }}>
				{props.files && !props.disableUpload && <input {...getInputProps({ ref: fileUploadRef })} style={{ display: 'none' }} />}
				{props.tableHeader}
				<TableViewComponent {...extendedProps} />
			</Box>
		);
	};

	const controlsProps = {
		...props,
		areFiltersPresent,
		fileUploadRef,
		getSavedLists,
		listViewID,
		mobile,
		setTableView,
		showTableControlsDivider,
		handleFilters,
		iconButtonOnClick: () => dispatch(toggleDrawer(DRAWER_CONTENT_TYPES.FILTERS))
	};

	return (
		<>
			<Box component="search">
				{!mobile && (props.children ? props.children({ Controls: Controls.bind(this, controlsProps) }) : <Controls {...controlsProps} />)}

				{!props.files && <FiltersList {...props.common} mui filters={props.filters} defaultFilters={props.defaultFilters} handler={handleFilters} />}

				{/* Filter Chips */}
				{areFiltersPresent && !props.hideFilters && <ChipContainer>{renderChips()}</ChipContainer>}

				{/* Breadcrumbs */}
				{props.files && props.breadcrumbs && props.breadcrumbs.length > 1 && <Breadcrumbs items={props.breadcrumbs} onBreadcrumbDrop={props.onBreadcrumbDrop} />}

				{/* Portal is created in <PageHeader /> for mobile only */}
				{showTableControls &&
					document.getElementById('table-view-controls') &&
					createPortal(
						<Box sx={{ display: 'flex' }}>{!isEmpty(props.views) && <TableView setTableView={setTableView} files={props.files} views={props.views} />}</Box>,
						document.getElementById('table-view-controls')
					)}

				{props.files && mobile && (
					<Box mb={3} mr={4} textAlign="right">
						Items showing:
						<Box ml={1} component="span" fontWeight={600}>
							{props.data.length}
						</Box>
					</Box>
				)}
			</Box>

			{renderTableView()}
		</>
	);
}

const ControlsContainer = styled(Box)(({ theme }) => ({
	top: 0,
	zIndex: 1,
	display: 'flex',
	position: 'sticky',
	alignItems: 'center',
	paddingTop: theme.spacing(2),
	paddingBottom: theme.spacing(2),
	marginBottom: theme.spacing(3),
	backgroundColor: theme.palette.common.white,
	[`& .${dividerClasses.root}`]: { marginRight: theme.spacing(4), borderColor: theme.palette.common.gray }
}));

const Controls = ({ areFiltersPresent, fileUploadRef, getSavedLists, handleFilters, iconButtonOnClick, listViewID, mobile, setTableView, showTableControlsDivider, ...props }) => {
	return (
		<ControlsContainer sx={{ mb: 0 }}>
			<Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'flex-end' }}>
				{/* Search */}
				{props.search && <Search {...props.common} placeholder={props.search} fetch={handleFilters} search={props.fetch} />}

				{/* Filters */}
				{props.filters && (
					<Tooltip
						title={
							<Box data-cy="table-filters-tooltip" component="div">
								Filters
							</Box>
						}
						arrow
						placement="bottom">
						<Box>
							<IconButton data-cy="table-filters-button" variant="regular" selected={areFiltersPresent} onClick={iconButtonOnClick}>
								{areFiltersPresent ? 'filter_alt' : 'filter_alt_off'}
							</IconButton>
						</Box>
					</Tooltip>
				)}

				{/* Saved Searches */}
				{getSavedLists && <SavedSearch {...props.common} fetch={handleFilters} />}

				{/* Settings Controls */}
				<Box sx={{ display: { xs: 'none', sm: 'contents' } }}>
					{!mobile && props.files ? <Files disableUpload={props.disableUpload} openFileUpload={() => fileUploadRef.current.click()} /> : <Settings {...props.common} />}
				</Box>

				{/* Table View */}
				{!isEmpty(props.views) && (
					<>
						{showTableControlsDivider && !props.children && <Divider orientation="vertical" variant="middle" flexItem sx={{ mr: '0 !important' }} />}

						<TableView setTableView={setTableView} files={props.files} views={props.views} />

						{props.children ? props.children : null}
					</>
				)}

				{/* Options */}
				<Options options={props.options} />
			</Box>
		</ControlsContainer>
	);
};
